import { Injectable, Optional } from "@angular/core";

import { TranslateService } from "@ngx-translate/core";

import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { TableDefinition, BfxColDef } from "./TableDefinition";

//**********************************************/

// real service imports

import { CcyLookupService } from "../lookup/model/impl/CcyLookupService";
import { CcyPairLookupService } from "../lookup/model/impl/CcyPairLookupService";
import { ClientLookupService } from "../lookup/model/impl/ClientLookupService";
import { AccountLookupService } from "../lookup/model/impl/AccountLookupService";
import { TraderGroupLookupService } from "../lookup/model/impl/TraderGroupLookupService";
import { SalesGroupLookupService } from "../lookup/model/impl/SalesGroupLookupService";
import { UserLookupService } from "../lookup/model/impl/UserLookupService";
import { SystemConfigPromiseService } from "shared/auth-services";
import { ProfilePromiseActionService } from "../profile/impl/ProfilePromiseActionService";

import { TradeTableDefinitionFactory } from "./TradeTableDefinitionFactory";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { AlgoStrategyLookupService } from "../lookup/model/impl/AlgoStrategyLookupService";
import { ValueFormatterParams, ValueGetterParams } from "ag-grid-community";

import { ClientModelPrivatization } from "./privatization/ClientModelPrivatization";
import { AccountModelPrivatization } from "./privatization/AccountModelPrivatization";
import { ClientTraderModelPrivatization } from "./privatization/ClientTraderModelPrivatization";
import { AtdlTooltipUiRep } from "./adtl/atdl-tooltip-ui-rep";
import { BfxSysConfigUtilService } from "shared/auth-services";
import { BfxConfigService } from "shared/auth-services/system-config/bfx-config.service";
import { CustomFieldLookupService } from "../lookup/model/impl/CustomFieldLookupService";
import { IndexType } from "./IndexType";
import { BfxConfigIDRefHelper } from "shared/helper-services/bfx-config-id-ref-helper";
import { ColumnFilterType } from "./ColumnFilterType";
import { COLUMN_TRADE_ORDER_PRODUCT } from "./TradeTableDefinitionFactory.constants";
import {
  FIXING_TIME_UTC_VALUE_FORMATTER,
  FIXING_TIME_UTC_VALUE_GETTER,
  FIXING_TIME_VALUE_GETTER,
} from "./BmarkHedgeOrdersTableDefinitionFactory.constants";
import { EMPTY_AMT_RATE_VALUE } from "shared/cubiq-services/model/BaseTableDefinitionFactory";

@Injectable()
export class ExtendedTradeTableDefinitionFactory extends TradeTableDefinitionFactory {
  constructor(
    profileService: ProfilePromiseActionService,
    protected atdlTooltipUiRep: AtdlTooltipUiRep,
    // It is not possible to inject against an interface
    @Optional() _ccyLookupService: CcyLookupService,
    @Optional() _ccyPairLookupService: CcyPairLookupService,
    @Optional() _clientLookup: ClientLookupService,
    @Optional() _accountLookup: AccountLookupService,
    @Optional() _salesGroupLookup: SalesGroupLookupService,
    @Optional() _traderGroupLookup: TraderGroupLookupService,
    @Optional() _userLookup: UserLookupService,
    @Optional() _sysConfigService: SystemConfigPromiseService,
    @Optional() _translateService: TranslateService,
    @Optional() algoStrategyLookupService: AlgoStrategyLookupService,
    @Optional() cmp: ClientModelPrivatization,
    @Optional() amp: AccountModelPrivatization,
    @Optional() ctmp: ClientTraderModelPrivatization,
    @Optional() sysConfigUtil: BfxSysConfigUtilService,
    @Optional() configService: BfxConfigService,
    @Optional() customFieldLookupService: CustomFieldLookupService,
  ) {
    super(
      profileService,
      atdlTooltipUiRep,
      _salesGroupLookup,
      _traderGroupLookup,
      _userLookup,
      _sysConfigService,
      _translateService,
      algoStrategyLookupService,
      _ccyPairLookupService,
      _ccyLookupService,
      _clientLookup,
      _accountLookup,
      cmp,
      amp,
      ctmp,
      sysConfigUtil,
      configService,
      customFieldLookupService,
    );
    this.enablePortfolio = true;
  }

  public createDef(): TableDefinition {
    let td = super.createDef();

    // Aggregate col only for risk trades and Find deals
    const aggregateCol = this.createAggregateIdCol();
    td.addColumn(aggregateCol);

    td = this.addBMTradeDef(td);
    td = this.addRiskTradeDef(td);

    return td;
  }

  protected addBMTradeDef(td: TableDefinition): TableDefinition {
    let position = this.position;
    let selCol: BfxColDef = td.columns[td.columnIndexMap.get("selection")];
    if (selCol) {
      // td.columns[td.columnIndexMap.get("selection")].adapter = new SelectionAdapter();
      selCol.width = 76;
      selCol.minWidth = this.threeWs;
      selCol.maxWidth = 76;
    }
    // TODO - NB - **
    // change value getter
    // let dealStateColumn: ColumnDefinition = td.getColumnDefinition('dealStateId');
    // if(dealStateColumn != undefined)
    //     dealStateColumn.adapter = new FindDealStateAdapter(this._translateService);
    let fixingDateColumn: BfxColDef = td.columns[td.columnIndexMap.get("fixingDate")];
    //can be null if NDFEnabled = false
    if (fixingDateColumn) {
      fixingDateColumn.minWidth = this.oneWs;
      fixingDateColumn.width = this.nineWs;
    }

    let productColumn: BfxColDef = td.columns[td.columnIndexMap.get(COLUMN_TRADE_ORDER_PRODUCT)];
    if (productColumn != null) {
      productColumn.valueGetter = (params: ValueGetterParams) => {
        if (params.data && params.data.spotReservationFg === true) {
          return "Spot Reserve";
        }

        return params.data[COLUMN_TRADE_ORDER_PRODUCT];
      };
    }

    // Reset here for new columns
    this.position.set(200);

    // Spot deal id
    let spotDealId: BfxColDef = this.initColdDef();
    spotDealId.headerName = "Spot Deal ID";
    spotDealId.field = "spotDealId";
    spotDealId.minWidth = this.oneWs;
    spotDealId.width = BfxConfigIDRefHelper.getDealRefWs();
    spotDealId.indexType = IndexType.Integer;
    spotDealId.columnFilterType = ColumnFilterType.NONE;

    if (!this.restoreFromConfig(spotDealId)) {
      spotDealId.hide = true;
      spotDealId.position = this.position.next();
    }

    spotDealId.valueGetter = (params) => {
      let spotDealRef: string = "";

      if (params.data.dealRef != params.data.spotDealRef) {
        spotDealRef = params.data.spotDealRef;
      }

      return spotDealRef;
    };

    td.addColumn(spotDealId);

    let fixingTimeColumn: BfxColDef = this.initColdDef();

    fixingTimeColumn.headerName = "Fixing Time";
    fixingTimeColumn.field = "reqFixingTime";
    fixingTimeColumn.columnFilterType = ColumnFilterType.NONE;
    fixingTimeColumn.minWidth = this.oneWs;
    fixingTimeColumn.width = this.fiveWs;
    fixingTimeColumn.cellClass = ["blotter_center_align_col"];
    fixingTimeColumn.valueGetter = FIXING_TIME_VALUE_GETTER.bind(this);
    if (!this.restoreFromConfig(fixingTimeColumn)) {
      fixingTimeColumn.hide = true;
      fixingTimeColumn.position = position.next();
    }

    td.addColumn(fixingTimeColumn);

    /**
     * Fixing Time in UTC terms column
     */
    let fixingTimeUTCColumn: BfxColDef = this.initColdDef();

    fixingTimeUTCColumn.headerName = "Fixing UTC Time";
    fixingTimeUTCColumn.field = "fixingTimeUTC";
    fixingTimeUTCColumn.headerTooltip = "Fixing Time in UTC Terms";
    fixingTimeUTCColumn.minWidth = this.oneWs;
    fixingTimeUTCColumn.width = this.eightWs;
    fixingTimeUTCColumn.cellClass = ["blotter_center_align_col"];
    fixingTimeUTCColumn.indexType = IndexType.String;
    fixingTimeUTCColumn.columnFilterType = ColumnFilterType.NONE;

    fixingTimeUTCColumn.valueGetter = FIXING_TIME_UTC_VALUE_GETTER.bind(this);
    fixingTimeUTCColumn.valueFormatter = FIXING_TIME_UTC_VALUE_FORMATTER.bind(this);

    if (!this.restoreFromConfig(fixingTimeUTCColumn)) {
      fixingTimeUTCColumn.hide = true;
      fixingTimeUTCColumn.position = position.next();
    }
    td.addColumn(fixingTimeUTCColumn);

    return td;
  }

  addRiskTradeDef(td: TableDefinition): TableDefinition {
    // External Primary Exec ID (FIX Field 17)
    let makerDealRef: BfxColDef = this.initColdDef();
    makerDealRef.headerName = "External Primary Exec ID";
    makerDealRef.field = "makerDealRef";
    makerDealRef.visibleInEmbeddedMode = true;
    makerDealRef.minWidth = this.oneWs;
    makerDealRef.width = BfxConfigIDRefHelper.getDealRefWs();
    makerDealRef.indexType = IndexType.String;
    makerDealRef.columnFilterType = ColumnFilterType.NONE;
    makerDealRef.colSelectorName = "Ext Primary Exec ID";

    if (!this.restoreFromConfig(makerDealRef)) {
      makerDealRef.hide = true;
      makerDealRef.position = this.position.next();
    }

    td.addColumn(makerDealRef);

    // External Secondary Exec ID (FIX Field 527)
    let secondaryMakerDealRef: BfxColDef = this.initColdDef();
    secondaryMakerDealRef.headerName = "External Secondary Exec ID";
    secondaryMakerDealRef.field = "secondaryMakerDealRef";
    secondaryMakerDealRef.minWidth = this.oneWs;
    secondaryMakerDealRef.visibleInEmbeddedMode = true;
    secondaryMakerDealRef.width = BfxConfigIDRefHelper.getDealRefWs();
    secondaryMakerDealRef.indexType = IndexType.String;
    secondaryMakerDealRef.columnFilterType = ColumnFilterType.NONE;
    secondaryMakerDealRef.colSelectorName = "Ext Second Exec ID";

    if (!this.restoreFromConfig(secondaryMakerDealRef)) {
      secondaryMakerDealRef.hide = true;
      secondaryMakerDealRef.position = this.position.next();
    }

    td.addColumn(secondaryMakerDealRef);

    return td;
  }

  protected tradeTraderGroupValueGetter(params: ValueFormatterParams): string {
    let result: string = super.tradeTraderGroupValueGetter(params);

    let deal: CubiqTS.DealImpl = params.data as CubiqTS.DealImpl;
    if (
      deal != null &&
      deal.ownerGroupIds != null &&
      deal.traderGroupId != null &&
      deal.ownerGroupIds.length > 0 &&
      deal.orderType == CubiqTS.OrderType.BENCHMARK
    ) {
      // Now find the secondary trader group as the trader group in the list which is not the one in the trader group field
      let primaryTraderGroupId: string = deal.traderGroupId.toString();
      let secondaryOwnerIds: Array<CubiqTS.Long> = deal.ownerGroupIds.filter(
        (value: CubiqTS.Long) => value.toString() != primaryTraderGroupId,
      );
      if (secondaryOwnerIds.length == 1) {
        let secondaryTraderGroup: CubiqTS.TradingGroupImpl = this._traderGroupLookup.getTraderGroup(
          secondaryOwnerIds[0],
        );
        if (secondaryTraderGroup != null) {
          result = result + " / " + secondaryTraderGroup.groupCode;
        }
      }
    }

    return result;
  }

  /**
   * Override to ensure that the top-level blotter also shows full amount precision
   * @param ccy
   * @param params
   * @returns
   */
  protected override formatCcyAmount(ccy: string, params: ValueFormatterParams): string {
    const dp = this.getCcyAmountPrecision(ccy);

    if (params.value != null && !(params.value as CubiqTS.BigNumber).eq(0)) {
      return (params.value as CubiqTS.BigNumber).toFormat(dp);
    }

    return EMPTY_AMT_RATE_VALUE;
  }
}
